// Inquiry Answer (PRODUCT_DEV-635) — 問い合わせへの AI 回答ドラフト生成 jobKind 契約。
//
// 共有エージェントジョブ基盤 (agent-jobs/contract.ts) の上に 1 つの jobKind として実装する
// （feature 専用エンドポイントは作らず、unizo API がサーバサイドで台帳へ enqueue する）。
// idea-review と同型。回答は「ドラフト」であり、運営が Admin 画面で確認・編集して承認するまで
// ユーザには届かない（自動即返信しない）。
//
// ここでは AgentJob.input / AgentJob.result の payload 形のみ定義する（スコープ・保存先・
// ライフサイクルは Layer1 のジョブレコードが担う）。
//
// TODO(shared-domain governance): LlmMetaSchema の正本が dev-lab 配下にあり product 横断 import に
// なっている。lib/llm-meta への昇格は別課題（idea-review と同じ相乗り）。
import { z } from "zod";
import { LlmMetaSchema } from "../dev-lab/llm-meta";

// ===== jobKind 定数（dispatcher は "inquiry-answer" prefix で routing） =====
export const INQUIRY_ANSWER_KIND = "inquiry-answer";

// ===== 問い合わせカテゴリ（入力補助 + プロンプトヒント + Slack/Admin 表示） =====
// fix3: 5 種に拡張。既存 3 値の id は保存済みデータ後方互換のため温存（ラベルのみ変更可）。
// enum 追加は ops worker の bundle 済み schema にも波及するため、新カテゴリ運用開始前に
// ops の再デプロイが必要（未デプロイだと feedback/other の AI ドラフトが validation failed）。
export const INQUIRY_CATEGORIES = [
  "howto",
  "bug",
  "request",
  "feedback",
  "other",
] as const;
export const InquiryCategorySchema = z.enum(INQUIRY_CATEGORIES);
export type InquiryCategory = z.infer<typeof InquiryCategorySchema>;

export const INQUIRY_CATEGORY_LABELS: Record<InquiryCategory, string> = {
  howto: "使い方がわからない",
  bug: "不具合報告",
  request: "要望",
  feedback: "感想",
  other: "その他",
};

// ===== やり取り（スレッド）と FAQ（PRODUCT_DEV-908） =====

/** 1件の問い合わせでやり取りできる通数の上限（1通目の本文・運営の回答を含む）。超えたら新しい問い合わせへ案内する。 */
export const INQUIRY_MAX_THREAD_LENGTH = 50;

/**
 * 409 の code（PRODUCT_DEV-908）。unizo / admin の API と画面で同じ文字列を使う。
 * THREAD_FULL = 50通に達した（または1件の記録が DynamoDB の大きさの上限に達した）。新しい問い合わせへ案内する。
 * CONFLICT = 見ていた状態と違う（ほかの人が先に書いた）。読み直してもらう。
 */
export const INQUIRY_THREAD_FULL_CODE = "INQUIRY_THREAD_FULL";
export const INQUIRY_CONFLICT_CODE = "INQUIRY_CONFLICT";

/** やり取りの1通の本文の上限（問い合わせ本文・回答と同じ）。 */
export const INQUIRY_MESSAGE_MAX_LENGTH = 4000;

export const InquiryMessageRoleEnum = z.enum(["user", "operator"]);
export type InquiryMessageRole = z.infer<typeof InquiryMessageRoleEnum>;

/** AI 下書きの入力に積むやり取りの1通（表示用の付帯情報は落とした形）。 */
export const InquiryAnswerThreadTurnSchema = z.object({
  role: InquiryMessageRoleEnum,
  text: z.string().max(INQUIRY_MESSAGE_MAX_LENGTH),
});
export type InquiryAnswerThreadTurn = z.infer<typeof InquiryAnswerThreadTurnSchema>;

export const INQUIRY_FAQ_QUESTION_MAX_LENGTH = 200;
export const INQUIRY_FAQ_ANSWER_MAX_LENGTH = 4000;

/**
 * プロンプト（AI 下書き・AI Q&A）に入れる FAQ の件数の上限。
 * EventBridge の1イベントは 256KB まで、台帳の1項目は 400KB までなので、件数だけでなく
 * packInquiryAnswerContext のバイト数の上限でも絞る。
 */
export const INQUIRY_FAQ_PROMPT_MAX_ITEMS = 50;

/** AI 下書きの入力に積む FAQ の1件。 */
export const InquiryAnswerFaqSchema = z.object({
  faqId: z.string().max(64),
  category: InquiryCategorySchema,
  question: z.string().max(INQUIRY_FAQ_QUESTION_MAX_LENGTH),
  answer: z.string().max(INQUIRY_FAQ_ANSWER_MAX_LENGTH),
});
export type InquiryAnswerFaq = z.infer<typeof InquiryAnswerFaqSchema>;

// ===== jobKind=inquiry-answer の input payload =====
// 問い合わせ本文は小さい（件名100字+本文4000字）ため inline。ガイドカタログは input に積まず
// worker が build 時に bundle する（台帳 DDB item 400KB 肥大防止・改ざんクライアント対策）。
export const InquiryAnswerInputSchema = z.object({
  inquiryId: z.string(),
  category: InquiryCategorySchema,
  title: z.string().max(100),
  body: z.string().max(4000),
  // 「解決しない場合は新しく問い合わせる」経由時の元問い合わせ（文脈引き継ぎ用）。
  relatedInquiry: z
    .object({
      title: z.string().max(100),
      body: z.string().max(4000),
      answerText: z.string().max(4000).optional(),
      // PRODUCT_DEV-908: 元の問い合わせの直近のやり取り（50通に達して引き継いだときに流れを渡す）。
      // 古い順。answerText（最初の回答）より後のもの。
      thread: z.array(InquiryAnswerThreadTurnSchema).max(INQUIRY_MAX_THREAD_LENGTH).optional(),
    })
    .optional(),
  // PRODUCT_DEV-908: 運営へ回す前に AI が利用者に出した回答（解決しなかったもの）。繰り返させない
  priorAiAnswer: z.string().max(4000).optional(),
  // 指示付き再生成（Admin 起点）のときのみ入る。現ドラフトを指示に従って改訂する。
  instruction: z.string().max(2000).optional(),
  currentDraft: z.string().max(4000).optional(),
  // PRODUCT_DEV-908: 1通目（title/body）より後のやり取り（運営の回答・利用者の追記・運営の返信）。
  // 古い順。送り手が packInquiryAnswerContext でバイト数を絞ってから積む（新しい方を残す）。
  // 未設定 = 1通目だけの問い合わせ（従来どおり）。
  thread: z.array(InquiryAnswerThreadTurnSchema).max(INQUIRY_MAX_THREAD_LENGTH).optional(),
  // PRODUCT_DEV-908: 公開中の FAQ（全テナント共通の1セット）。worker は UnizoInquiries と
  // 別アカウント（shared-services）にいて FAQ の表を読めないので、送り手が同梱する。
  faqs: z.array(InquiryAnswerFaqSchema).max(INQUIRY_FAQ_PROMPT_MAX_ITEMS).optional(),
});
export type InquiryAnswerInput = z.infer<typeof InquiryAnswerInputSchema>;

// ===== inquiry-answer の result payload（AgentJob.result に入る） =====
// references はガイド記事への構造化参照。URL 文字列を draftText に埋めず、web 側が
// /guide/article/{categoryId}/{articleId} を描画する（ハルシネーション URL 防止）。
//
// PRODUCT_DEV-908: FAQ も参照先にする。kind="faq" のときは articleId に faqId、categoryId に
// FAQ のカテゴリを入れ、web は /inquiries/faq#<faqId> を描画する。
// kind 未設定 = ガイド記事（PD-908 以前の保存済みデータとの互換）。
// PRODUCT_DEV-908: "page" = Unizo の常設ページ（unizo/inquiryLinkablePages.ts の一覧から選ぶ。
// articleId=ページの id・categoryId="page"）。運営の回答と FAQ の関連リンクにだけ使い、AI は出さない
export const InquiryAnswerReferenceKindEnum = z.enum(["guide", "faq", "page"]);

/** 関連リンク（運営の回答・返信、FAQ）に新しく付けられる数の上限。保存済みの5件までの参照はそのまま表示する */
export const INQUIRY_LINKS_MAX = 3;
export type InquiryAnswerReferenceKind = z.infer<
  typeof InquiryAnswerReferenceKindEnum
>;

export const InquiryAnswerReferenceSchema = z.object({
  kind: InquiryAnswerReferenceKindEnum.optional(),
  categoryId: z.string().max(64),
  articleId: z.string().max(64),
  title: z.string().max(200),
});
export type InquiryAnswerReference = z.infer<
  typeof InquiryAnswerReferenceSchema
>;

export const InquiryAnswerResultSchema = z.object({
  inquiryId: z.string(), // read-back 時の宛先ガード（result.inquiryId === 対象 inquiry）
  draftText: z.string().max(4000),
  references: z.array(InquiryAnswerReferenceSchema).max(5).default([]),
  meta: LlmMetaSchema.optional(),
});
export type InquiryAnswerResult = z.infer<typeof InquiryAnswerResultSchema>;

// 再生成で同一問い合わせに複数ジョブが立つため、jobId は inquiryId + 世代番号で構成する。
export const inquiryAnswerJobId = (inquiryId: string, rev: number): string =>
  `${inquiryId}-r${rev}`;

// ===== 統合点のイベント契約（PRODUCT_DEV-881）=====
//
// unizo は台帳へ直接 PutItem せず、**このイベントを出すだけ**にする。
// 受け側（ops）が jobId を決めて台帳へ積む。
//
// ## なぜ台帳を直接触らないのか
//
// 台帳は ops(単一env "ops") 所有で、unizo は**同一アカウント前提**の物理名復元で
// 参照していた。⑧で dev/stg が別アカウントへ出た時点でこの前提は破れ、
// **新 dev / 新 stg では実際に enqueue が失敗している**（fail-soft なので画面には出ない）。
//
// ## 「作られた」であって「ジョブを積め」ではない
//
// detail-type を `InquiryCreated` にしてあるのは、**何をするかを受け側に決めさせる**ため。
// いまは AI 回答ドラフトを1本積むだけだが、この先「過去QAと照合して一致すれば自動返信」を
// 足すときに、**unizo を一度も触らずに購読側だけを増やせる**。
// 逆に `EnqueueAnswerJob` のような命令形にすると、送り手が処理の中身を知ることになる。
//
// ## jobId を送らない
//
// `inquiryAnswerJobId(inquiryId, rev)` は台帳の都合なので受け側で決める。
// 初回は必ず rev=1（再生成は admin が別経路で積む）。
export const INQUIRY_EVENT_SOURCE = "umeee.unizo";
export const INQUIRY_CREATED_DETAIL_TYPE = "InquiryCreated";

/**
 * `InquiryCreated` の detail。
 *
 * `inquiry` は `InquiryAnswerInputSchema` をそのまま使う（両側が既に知っている正本を
 * 二重に持たない）。ただし `instruction` / `currentDraft` は**指示付き再生成専用**で、
 * このイベントには入らない。
 */
export const InquiryCreatedDetailSchema = z.object({
  tenantId: z.string().min(1),
  createdBy: z.string().min(1),
  inquiry: InquiryAnswerInputSchema,
});
export type InquiryCreatedDetail = z.infer<typeof InquiryCreatedDetailSchema>;

// ===== 追記のイベント契約（PRODUCT_DEV-908）=====
//
// 利用者が問い合わせに追記したら、unizo はこのイベントを出す（InquiryCreated と同じバス）。
// 受け側（ops）が AI 下書きを作り直す。
//
// InquiryCreated と違い初回ではないので、**世代番号 rev を送る**。unizo は追記と同じ
// UpdateItem で draftRev を +1 し、latestJobId = inquiryAnswerJobId(inquiryId, rev) を書く。
// admin の読み戻しはその jobId を見るので、受け側が別の世代で積むと下書きが永久に pending になる。
export const INQUIRY_MESSAGE_POSTED_DETAIL_TYPE = "InquiryMessagePosted";

export const InquiryMessagePostedDetailSchema = z.object({
  tenantId: z.string().min(1),
  createdBy: z.string().min(1),
  rev: z.number().int().min(2),
  inquiry: InquiryAnswerInputSchema,
});
export type InquiryMessagePostedDetail = z.infer<
  typeof InquiryMessagePostedDetailSchema
>;
